import { z } from 'zod'
import { optionalText, versionSchema } from './common.js'
import type { WeekPattern } from './working-calendar.js'

// ── working calendar API (ASM-141, recruitment/01 §5) ────────────────────────
//
// The week pattern is the `calendar` setting group (GET/PATCH /settings/calendar). Holidays are tenant
// data: whole Asia/Muscat business dates, at most one per date, between 2000 and 2100.

export const HOLIDAY_MIN_DATE = '2000-01-01'
export const HOLIDAY_MAX_DATE = '2100-12-31'
export const HOLIDAY_NAME_MAX = 120
/** Rows in one import (a year of holidays fits many times over). */
export const HOLIDAY_IMPORT_MAX = 400
/** The widest span GET /calendar answers (two years). */
export const CALENDAR_RANGE_MAX_DAYS = 732

const holidayDateSchema = z.iso
  .date()
  .refine((d) => d >= HOLIDAY_MIN_DATE && d <= HOLIDAY_MAX_DATE, 'Use a date between 2000 and 2100')

const yearSchema = z.coerce.number().int().min(2000).max(2100)

export const holidayInputSchema = z
  .object({
    date: holidayDateSchema,
    name: z.string().trim().min(1).max(HOLIDAY_NAME_MAX),
    nameAr: optionalText(HOLIDAY_NAME_MAX),
    provisional: z.boolean().default(false),
  })
  .strict()
export type HolidayInput = z.input<typeof holidayInputSchema>

export const holidayUpdateSchema = holidayInputSchema.extend({ version: versionSchema }).strict()
export type HolidayUpdate = z.input<typeof holidayUpdateSchema>

/**
 * POST /calendar/holidays/import: all rows or none. With `replaceYear`, that year's holidays are deleted
 * first and every row must fall inside it; without it, a row on a date already taken is refused (409).
 */
export const holidayImportSchema = z
  .object({
    rows: z.array(holidayInputSchema).min(1).max(HOLIDAY_IMPORT_MAX),
    replaceYear: yearSchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    const seen = new Set<string>()
    v.rows.forEach((row, i) => {
      if (seen.has(row.date)) {
        ctx.addIssue({ code: 'custom', path: ['rows', i, 'date'], message: 'This date appears twice' })
      }
      seen.add(row.date)
      if (v.replaceYear !== undefined && !row.date.startsWith(`${v.replaceYear}-`)) {
        ctx.addIssue({
          code: 'custom',
          path: ['rows', i, 'date'],
          message: `The date is outside ${v.replaceYear}`,
        })
      }
    })
  })
export type HolidayImport = z.input<typeof holidayImportSchema>

/** GET /calendar/holidays?year (default: the current Asia/Muscat year). */
export const holidayListQuerySchema = z.object({ year: yearSchema.optional() })
export type HolidayListQuery = z.input<typeof holidayListQuerySchema>

/** GET /calendar?from&to (default: today and a year on); both ends inclusive, at most two years apart. */
export const calendarQuerySchema = z
  .object({ from: holidayDateSchema.optional(), to: holidayDateSchema.optional() })
  .refine((v) => !v.from || !v.to || v.from <= v.to, { message: 'from must not be after to', path: ['to'] })
export type CalendarQuery = z.input<typeof calendarQuerySchema>

export interface Holiday {
  id: string
  date: string
  name: string
  nameAr: string | null
  provisional: boolean
  version: number
  updatedAt: string
}

export interface HolidayList {
  year: number
  items: Holiday[]
}

/** GET /calendar: the week pattern and the holidays between `from` and `to` (inclusive). */
export interface CalendarView {
  from: string
  to: string
  week: WeekPattern
  /** The `calendar` setting group's version (PATCH /settings/calendar needs it). */
  weekVersion: number
  holidays: Holiday[]
}

export interface HolidayImportResult {
  created: number
  /** Holidays of `replaceYear` deleted first (0 without it). */
  replaced: number
}
